import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { getMyEntities, switchEntity } from '../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function EntitySwitcher({ currentEntity, onEntitySwitch }) {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      setLoading(true);
      const { data } = await getMyEntities();
      setEntities(data.entities || []);
    } catch (error) {
      console.error('Failed to load entities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchEntity = async (entityId) => {
    if (entityId === currentEntity?.id) return;

    try {
      setSwitching(true);
      const { data } = await switchEntity(entityId);
      
      // Update token
      localStorage.setItem('token', data.token);
      
      // Update user context if callback provided
      if (onEntitySwitch) {
        onEntitySwitch(data.entity, data.role);
      }
      
      toast.success(`Switched to ${data.entity.name}`);
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch entity:', error);
      toast.error('Failed to switch entity');
    } finally {
      setSwitching(false);
    }
  };

  if (entities.length <= 1) {
    // Don't show switcher if user only has access to one entity
    return null;
  }

  const currentEntityData = entities.find(e => e.is_current) || currentEntity;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" disabled={switching}>
          <Building2 className="w-4 h-4" />
          <span className="hidden md:inline">{currentEntityData?.name || 'Select Entity'}</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Switch Entity</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {loading ? (
          <DropdownMenuItem disabled>
            Loading entities...
          </DropdownMenuItem>
        ) : (
          entities.map((entity) => (
            <DropdownMenuItem
              key={entity.id}
              onClick={() => handleSwitchEntity(entity.id)}
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex flex-col">
                  <span className="font-medium">{entity.name}</span>
                  <span className="text-xs text-muted-foreground">
                    Role: {entity.user_role}
                  </span>
                </div>
                {entity.is_current && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
